<template>
  <div
    class="training-section-info-block-item"
    :class="`image-position-${imagePosition}`"
  >
    <div
      v-if="image"
      class="info-block-image"
    >
      <BgMedia :media="getBackground()" />
    </div>
    <div class="info-block-content">
      <div
        v-if="text"
        class="info-block-text"
        v-html="markdownToHTML(text || '')"
      ></div>
    </div>
  </div>
</template>

<script>
import BgMedia from '@/components/BgMedia.vue';

// Markdown
const md = require('markdown-it')({
  html: true,
  linkify: true,
  typographer: true,
});

export default {
  name: 'TrainingSectionInfoBlockItem',
  components: {
    BgMedia,
  },
  props: {
    image: {
      type: Object,
      default: null,
    },
    imageMobile: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    imagePosition: {
      type: String,
      default: 'top',
      validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value),
    },
  },
  methods: {
    markdownToHTML(content) {
      return md.render(content);
    },
    getBackground() {
      return {
        background: this.image,
        backgroundMobile: this.imageMobile,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.training-section-info-block-item {
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  background-color: #F3F3F3;

  // Позиция сверху (по умолчанию)
  &.image-position-top {
    flex-direction: column;
  }

  // Позиция снизу
  &.image-position-bottom {
    flex-direction: column-reverse;
  }

  // Позиция слева
  &.image-position-left {
    @include screen("md", min) {
      flex-direction: row;
      align-items: stretch;
    }

    @include screen("md", max) {
      flex-direction: column;
    }

    @include wRule(
      gap,
      (
        xl: 100,
        lg: 80,
        md: 0,
        sm: 0,
      )
    );

    // На lg - изображение выходит за контейнер к левому краю viewport
    @include screen("lg", max) {
      @include screen("md", min) {
        @include wRule(
          margin-left,
          (lg: -165)
        );
      }
    }
  }

  // Позиция справа
  &.image-position-right {
    @include screen("md", min) {
      flex-direction: row-reverse;
      align-items: stretch;
    }

    @include screen("md", max) {
      flex-direction: column;
    }

    @include wRule(
      gap,
      (
        xl: 100,
        lg: 80,
        md: 0,
        sm: 0,
      )
    );

    // На lg - изображение выходит за контейнер к правому краю viewport
    @include screen("lg", max) {
      @include screen("md", min) {
        overflow: visible;

        .info-block-image {
          @include wRule(
            margin-right,
            (lg: -165)
          );
        }
      }
    }
  }

  .info-block-image {
    position: relative;
    display: block;
    overflow: hidden;
    width: 100%;
    flex-shrink: 0;
    padding-top: 0;

    @include wRule(
      height,
      (
        xl: 535,
        lg: 380,
        md: 335,
        sm: 400,
      )
    );

    // На md - изображение уже и справа
    @include screen("md", max) {
      @include screen("sm", min) {
        @include wRule(
          width,
          (
            md: 574,
          )
        );
        margin-left: auto;
        margin-right: 0;
      }
    }

    ::v-deep(.bg-media) {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  // Для горизонтальных позиций (left/right) - стили для изображения
  &.image-position-left,
  &.image-position-right {
    .info-block-image {
      @include screen("md", min) {
        flex: 0 0 48%;
        max-width: 48%;
        height: unset;
        aspect-ratio: 691 / 450;
      }

      // На lg - увеличенный flex для компенсации расширения за контейнер
      @include screen("lg", max) {
        @include screen("md", min) {
          flex: 0 0 57%;
          max-width: 57%;
        }
      }

      @include screen("md", max) {
        flex: none;
        max-width: 100%;
      }
    }

    .info-block-content {
      @include screen("md", min) {
        flex: 1;
        padding: 0;
      }
    }
  }

  .info-block-content {
    display: flex;
    flex-direction: column;
    position: relative;
    justify-content: center;

    @include wRule(
      padding-top,
      (
        xl: 50,
        lg: 45,
        md: 40,
        sm: 40,
      )
    );

    @include wRule(
      padding-bottom,
      (
        xl: 50,
        lg: 45,
        md: 40,
        sm: 40,
      )
    );

    // Отступ слева для вертикального сепаратора (только xl/lg)
    @include screen("md", min) {
      @include wRule(
        padding-left,
        (
          xl: 150,
          lg: 109,
        )
      );
    }

    // Горизонтальные отступы для md/sm (вертикальный режим)
    @include screen("md", max) {
      @include wRule(
        padding-left,
        (
          md: 0,
          sm: 24,
        )
      );
      @include wRule(
        padding-right,
        (
          md: 0,
          sm: 24,
        )
      );
    }
  }

  // Сепаратор (вертикальная на xl/lg, горизонтальная на md/sm)
  .info-block-content::before {
    content: '';
    position: absolute;
    background-color: #2b2f33;
    opacity: 0.7;

    @include screen("md", min) {
      top: 0;
      bottom: 0;
      width: 2px;
      height: auto;
      left: 0;
    }

    @include screen("md", max) {
      display: none;
    }
  }

  // Позиция справа - сепаратор слева от текста
  &.image-position-right .info-block-content {
    @include screen("md", min) {
      @include wRule(
        padding-left,
        (
          xl: 150,
          lg: 109,
        )
      );
      padding-right: 0;
    }
  }

  &.image-position-right .info-block-content::before {
    @include screen("md", min) {
      left: 0;
      right: auto;
    }
  }

  // Позиция слева - сепаратор справа от текста
  &.image-position-left .info-block-content {
    @include screen("md", min) {
      padding-left: 0;
      @include wRule(
        padding-right,
        (
          xl: 120,
          lg: 90,
        )
      );
    }
  }

  &.image-position-left .info-block-content::before {
    @include screen("md", min) {
      left: auto;
      right: 0;
    }
  }

  // Позиции top и bottom - сепаратор всегда слева
  &.image-position-top .info-block-content::before,
  &.image-position-bottom .info-block-content::before {
    @include screen("md", min) {
      left: 0;
      right: auto;
    }
  }

  .info-block-text {
    font-weight: 400;
    line-height: 1.6;

    // На md - ограниченная ширина текста
    @include screen("md", max) {
      @include screen("sm", min) {
        @include wRule(
          max-width,
          (
            md: 507,
          )
        );
      }
    }

    @include wRule(
      font-size,
      (
        xl: 18,
        lg: 16,
        md: 14,
        sm: 28,
      )
    );

    ::v-deep(p) {
      margin: 0;

      &:not(:last-child) {
        @include wRule(
          margin-bottom,
          (
            xl: 16,
            lg: 14,
            md: 12,
            sm: 20,
          )
        );
      }
    }

    ::v-deep(ul),
    ::v-deep(ol) {
      margin: 0;
      padding-left: 1.5em;

      &:not(:last-child) {
        @include wRule(
          margin-bottom,
          (
            xl: 16,
            lg: 14,
            md: 12,
            sm: 20,
          )
        );
      }
    }

    ::v-deep(li) {
      &:not(:last-child) {
        @include wRule(
          margin-bottom,
          (
            xl: 8,
            lg: 7,
            md: 6,
            sm: 10,
          )
        );
      }
    }
  }
}
</style>
